import React, { useEffect, useState } from "react";
import { ordersApi } from "../services/orders";
import type { TrendingData } from "../types/orders";
import "./DJLeaderboard.css";
import LeaderboardFlame from "./LeaderboardFlame";
import { absMediaUrl } from "../utils/mediaUrl";
import { t } from "../i18n";
import { T } from "../i18n/T";
import { useAccountCache } from "../hooks/useAccountCache";

// Rolling time filters — keys match the server's /orders/trending ?range= enum.
const RANGES = [
  { key: "week", label: "1W" },
  { key: "month", label: "1M" },
  { key: "3months", label: "3M" },
  { key: "year", label: "1Y" },
  { key: "all", label: t("dJLeaderboard.all") },
] as const;
type RangeKey = (typeof RANGES)[number]["key"];

// Defensive parse — never trust the payload shape (endpoint may evolve).
// Shared with the homepage's Trending now strip (home/TrendingNow).
export function parseTrending(raw: any): TrendingData {
  const songs = Array.isArray(raw?.songs) ? raw.songs : [];
  const artists = Array.isArray(raw?.artists) ? raw.artists : [];
  return {
    songs: songs
      .filter((s: any) => s && typeof s.query === "string")
      .slice(0, 20)
      .map((s: any) => ({
        query: s.query,
        artists: Array.isArray(s.artists)
          ? s.artists.filter((a: any) => typeof a === "string")
          : [],
        image: typeof s.image === "string" ? s.image : null,
        total: Number(s.total) || 0,
        trackId: typeof s.trackId === "string" && s.trackId ? s.trackId : null,
      })),
    artists: artists
      .filter((a: any) => a && typeof a.name === "string")
      .slice(0, 20)
      .map((a: any) => ({
        name: a.name,
        image: typeof a.image === "string" ? a.image : null,
        total: Number(a.total) || 0,
      })),
  };
}

export const Artwork: React.FC<{ image: string | null }> = ({ image }) => {
  const src = absMediaUrl(image);
  return src ? (
    <img className="djlb-art" src={src} alt="" />
  ) : (
    <span className="djlb-art djlb-art--fallback" aria-hidden="true">
      ♪
    </span>
  );
};

// The email chart look (founder pick A, build 44): #1 glows green with a
// flame, the top three ranks are gold / silver / bronze, rows slide in one by
// one. Shared with the Top Talent and Top Fans boards.
export const rowClass = (i: number) => `djlb-row${i === 0 ? " djlb-row--top" : ""}`;
export const rankClass = (i: number) => `djlb-rank${i < 3 ? ` djlb-rank--${i + 1}` : ""}`;
export const rowIndex = (i: number) => ({ "--i": i }) as React.CSSProperties;

const SkeletonRows: React.FC = () => (
  <div className="djlb-skel" aria-hidden="true">
    {Array.from({ length: 5 }).map((_, i) => (
      <div className="djlb-skel-row" key={i} />
    ))}
  </div>
);

interface DJLeaderboardProps {
  /** The dedicated /leaderboard page provides its own header + back arrow. */
  showHeader?: boolean;
}

const DJLeaderboard: React.FC<DJLeaderboardProps> = ({ showHeader = true }) => {
  const [range, setRange] = useState<RangeKey>("all");
  const [list, setList] = useState<"songs" | "artists">("songs");
  // Kept for the session (hooks/useAccountCache): the board reopens with the
  // charts it showed last time, then refreshes the open range quietly.
  const [cache, setCache] = useAccountCache<Partial<Record<RangeKey, TrendingData>>>("djlb", {});
  const [refreshed] = useState(() => new Set<RangeKey>());
  // Endpoint missing/erroring on first load (e.g. server not deployed yet)
  // → hide the whole section silently.
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (unavailable || refreshed.has(range)) return;
    let cancelled = false;
    const hadData = Object.keys(cache).length > 0;
    ordersApi
      .trending(range)
      .then((res) => {
        if (cancelled) return;
        refreshed.add(range);
        setCache((c) => ({ ...c, [range]: parseTrending(res) }));
      })
      .catch(() => {
        if (cancelled) return;
        refreshed.add(range);
        if (!hadData) setUnavailable(true);
        // A later range failing after a successful load shows the empty
        // state for that range instead of nuking the whole section.
        else setCache((c) => (c[range] ? c : { ...c, [range]: { songs: [], artists: [] } }));
      });
    return () => {
      cancelled = true;
    };
    // Once per range per visit; the cache only paints meanwhile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, unavailable]);

  if (unavailable) return null;

  const data = cache[range];
  const isEmpty = !!data && data.songs.length === 0 && data.artists.length === 0;

  return (
    <div className={showHeader ? "djlb-section" : "djlb-section djlb-section--bare"}>
      {showHeader && (
        <div className="djlb-header">
          <span className="djlb-title">
            <T k="dJLeaderboard.songRequestLeaderboard" tags={{ 0: () => <LeaderboardFlame size={18} /> }} />
          </span>
        </div>
      )}

      <div className="djlb-chips" role="group" aria-label={t("dJLeaderboard.timeRange")}>
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            className={`djlb-chip${range === r.key ? " djlb-chip--active" : ""}`}
            aria-pressed={range === r.key}
            onClick={() => setRange(r.key)}
          >
            {r.label}
          </button>
        ))}
      </div>

      {isEmpty ? (
        <div className="djlb-empty">{t("dJLeaderboard.noRequestsInThis")}</div>
      ) : (
        <div className="djlb-cards">
          {/* One list at a time, the switch in the card's head (founder,
              1.5.2 b49: "a top artists toggle beside top songs"): two tabs,
              the open one bold over a green line that slides between them. */}
          <div className="djlb-card">
            <div className="djlb-card-title djlb-card-title--switch">
              <div className="djlb-switch" role="tablist" aria-label={t("dJLeaderboard.songsOrArtists")}>
                {(["songs", "artists"] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    aria-selected={list === k}
                    className={`djlb-switch-btn${list === k ? " djlb-switch-btn--active" : ""}`}
                    onClick={() => setList(k)}
                  >
                    {k === "songs" ? t("dJLeaderboard.switchSongs") : t("dJLeaderboard.switchArtists")}
                  </button>
                ))}
                <span className={`djlb-switch-ink${list === "artists" ? " djlb-switch-ink--artists" : ""}`} aria-hidden="true" />
              </div>
            </div>
            {list === "songs" ? (
              <>
                {!data ? (
                  <SkeletonRows />
                ) : data.songs.length === 0 ? (
                  <div className="djlb-empty">{t("dJLeaderboard.noRequestsInThis")}</div>
                ) : (
                  <ol className="djlb-list">
                    {data.songs.map((s, i) => (
                      <li className={rowClass(i)} key={`${s.query}-${i}`} style={rowIndex(i)}>
                        <span className={rankClass(i)}>{i + 1}</span>
                        {s.trackId ? (
                          <a
                            className="djlb-artlink"
                            href={`https://open.spotify.com/track/${s.trackId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={t("dJLeaderboard.openOnSpotify", { query: s.query })}
                          >
                            <Artwork image={s.image} />
                          </a>
                        ) : (
                          <Artwork image={s.image} />
                        )}
                        <span className="djlb-meta">
                          <span className="djlb-name">{s.query}</span>
                          {s.artists.length > 0 && (
                            <span className="djlb-sub">{s.artists.join(", ")}</span>
                          )}
                        </span>
                        <span className="djlb-count">{i === 0 && <LeaderboardFlame size={17} live />}{s.total}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </>
            ) : (
              <>
                {!data ? (
                  <SkeletonRows />
                ) : data.artists.length === 0 ? (
                  <div className="djlb-empty">{t("dJLeaderboard.noRequestsInThis")}</div>
                ) : (
                  <ol className="djlb-list">
                    {data.artists.map((a, i) => (
                      <li className={rowClass(i)} key={`${a.name}-${i}`} style={rowIndex(i)}>
                        <span className={rankClass(i)}>{i + 1}</span>
                        <Artwork image={a.image} />
                        <span className="djlb-meta">
                          <span className="djlb-name">{a.name}</span>
                        </span>
                        <span className="djlb-count">{i === 0 && <LeaderboardFlame size={17} live />}{a.total}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DJLeaderboard;
